// ClientContracts — عقود العملاء (3.4.12): قائمة + إنشاء/تعديل + تذكير الانتهاء.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid,
  MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { hasActionPermission } from '../../utils/accessControl';
import { SmartPage, SmartHeader } from '../../components/smart';
import { SMART_COLORS as C } from '../../theme/smartStableTheme';

const TYPES = ['boarding', 'breeding', 'services', 'other'];
const STATUSES = ['draft', 'active', 'expired', 'terminated'];
const emptyContract = { contract_type: 'services', title: '', party_name: '', start_date: '', end_date: '', value: '', status: 'active', terms: '' };
const statusColor = { active: '#2e7d32', draft: '#777', expired: '#b8860b', terminated: '#c62828' };

export default function ClientContracts() {
  const { t } = useTranslation();
  const canManage = hasActionPermission('contracts-manage');
  const [rows, setRows] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const [listRes, expRes] = await Promise.all([
        api.get('/contracts', { params: { contract_type: typeFilter || undefined, status: statusFilter || undefined, q: search || undefined } }),
        api.get('/contracts/expiring', { params: { days: 30 } }),
      ]);
      setRows(Array.isArray(listRes.data) ? listRes.data : listRes.data?.data || []);
      setExpiring(expRes.data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    }
  }, [typeFilter, statusFilter, search]);

  useEffect(() => {
    const id = setTimeout(load, 300);
    return () => clearTimeout(id);
  }, [load]);

  const save = async () => {
    try {
      const f = dialog.form;
      if (f.contract_id) await api.put(`/contracts/${f.contract_id}`, f);
      else await api.post('/contracts', f);
      setDialog(null);
      load();
    } catch (e) { setError(e?.response?.data?.message || e.message); }
  };

  // 3.4.27: رفع/عرض نسخة PDF موقّعة (للعقد المحفوظ فقط).
  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !dialog?.form?.contract_id) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/contracts/${dialog.form.contract_id}/file`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDialog((d) => ({ form: { ...d.form, file_path: res.data?.file_path } }));
      load();
    } catch (err) { setError(err?.response?.data?.message || err.message); }
  };
  const viewFile = async (id) => {
    try {
      const res = await api.get(`/contracts/${id}/file`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) { setError(err?.response?.data?.message || err.message); }
  };

  const typeLabel = useMemo(() => ({
    boarding: t('boarding', 'إيواء'), breeding: t('breeding', 'إنتاج'),
    services: t('services', 'خدمات'), other: t('other', 'أخرى'),
  }), [t]);

  return (
    <SmartPage>
      <SmartHeader title={t('client_contracts', 'عقود العملاء')} subtitle={t('contracts_hint', 'عقود الإيواء/الإنتاج/الخدمات: أطراف، مدد، قيمة، حالة، وتذكير انتهاء.')} />

      {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert> : null}

      {expiring.length ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('contracts_expiring', 'عقود تنتهي قريبًا')}: {expiring.map((c) => `${c.title} (${String(c.end_date).slice(0, 10)})`).join(' · ')}
        </Alert>
      ) : null}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
        <TextField select size="small" label={t('type', 'النوع')} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} sx={{ width: 160 }}>
          <MenuItem value="">{t('all', 'الكل')}</MenuItem>
          {TYPES.map((ty) => <MenuItem key={ty} value={ty}>{typeLabel[ty]}</MenuItem>)}
        </TextField>
        <TextField select size="small" label={t('status', 'الحالة')} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ width: 160 }}>
          <MenuItem value="">{t('all', 'الكل')}</MenuItem>
          {STATUSES.map((s) => <MenuItem key={s} value={s}>{t(s, s)}</MenuItem>)}
        </TextField>
        <TextField size="small" label={t('search', 'بحث')} value={search} onChange={(e) => setSearch(e.target.value)} sx={{ width: 240 }} />
        <Box sx={{ flex: 1 }} />
        {canManage ? (
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => setDialog({ form: { ...emptyContract } })}
            sx={{ bgcolor: C.appbar, color: '#fff', fontWeight: 900, '&:hover': { bgcolor: C.appbarHover } }}>
            {t('add_contract', 'عقد جديد')}
          </Button>
        ) : null}
      </Stack>

      <Box sx={{ overflowX: 'auto', border: `1px solid ${C.line}`, borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: C.cardSoft }}>
              {[t('title', 'العنوان'), t('type', 'النوع'), t('party', 'الطرف'), t('owner', 'المالك'), t('period', 'المدة'), t('value', 'القيمة'), t('status', 'الحالة'), ''].map((h, i) => (
                <TableCell key={i} sx={{ fontWeight: 900 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ color: C.muted, py: 3 }}>{t('no_contracts', 'لا عقود')}</TableCell></TableRow>
            ) : rows.map((c) => (
              <TableRow key={c.contract_id}>
                <TableCell sx={{ fontWeight: 700 }}>{c.title}</TableCell>
                <TableCell>{typeLabel[c.contract_type] || c.contract_type}</TableCell>
                <TableCell>{c.party_name || '—'}</TableCell>
                <TableCell>{c.owner_name?.trim() || '—'}</TableCell>
                <TableCell>{[c.start_date, c.end_date].map((d) => (d ? String(d).slice(0, 10) : '—')).join(' → ')}</TableCell>
                <TableCell>{c.value != null ? Number(c.value).toLocaleString('en-US') : '—'}</TableCell>
                <TableCell><Chip size="small" label={t(c.status, c.status)} sx={{ bgcolor: statusColor[c.status] || C.line, color: '#fff', fontWeight: 800 }} /></TableCell>
                <TableCell align="right">{canManage ? <Button size="small" onClick={() => setDialog({ form: { ...c, start_date: c.start_date ? String(c.start_date).slice(0, 10) : '', end_date: c.end_date ? String(c.end_date).slice(0, 10) : '' } })}>{t('edit', 'تعديل')}</Button> : null}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Dialog open={Boolean(dialog)} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog?.form?.contract_id ? t('edit_contract', 'تعديل عقد') : t('add_contract', 'عقد جديد')}</DialogTitle>
        <DialogContent>
          {dialog ? (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12}><TextField fullWidth size="small" label={t('title', 'العنوان')} value={dialog.form.title} onChange={(e) => setDialog((d) => ({ form: { ...d.form, title: e.target.value } }))} /></Grid>
              <Grid item xs={6}>
                <TextField select fullWidth size="small" label={t('type', 'النوع')} value={dialog.form.contract_type} onChange={(e) => setDialog((d) => ({ form: { ...d.form, contract_type: e.target.value } }))}>
                  {TYPES.map((ty) => <MenuItem key={ty} value={ty}>{typeLabel[ty]}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField select fullWidth size="small" label={t('status', 'الحالة')} value={dialog.form.status} onChange={(e) => setDialog((d) => ({ form: { ...d.form, status: e.target.value } }))}>
                  {STATUSES.map((s) => <MenuItem key={s} value={s}>{t(s, s)}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}><TextField fullWidth size="small" label={t('party', 'الطرف')} value={dialog.form.party_name || ''} onChange={(e) => setDialog((d) => ({ form: { ...d.form, party_name: e.target.value } }))} /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth size="small" type="number" label={t('value', 'القيمة')} value={dialog.form.value ?? ''} onChange={(e) => setDialog((d) => ({ form: { ...d.form, value: e.target.value } }))} /></Grid>
              <Grid item xs={6}><TextField fullWidth size="small" type="date" label={t('start', 'البداية')} InputLabelProps={{ shrink: true }} value={dialog.form.start_date || ''} onChange={(e) => setDialog((d) => ({ form: { ...d.form, start_date: e.target.value } }))} /></Grid>
              <Grid item xs={6}><TextField fullWidth size="small" type="date" label={t('end', 'النهاية')} InputLabelProps={{ shrink: true }} value={dialog.form.end_date || ''} onChange={(e) => setDialog((d) => ({ form: { ...d.form, end_date: e.target.value } }))} /></Grid>
              <Grid item xs={12}><TextField fullWidth size="small" multiline minRows={2} label={t('terms', 'البنود')} value={dialog.form.terms || ''} onChange={(e) => setDialog((d) => ({ form: { ...d.form, terms: e.target.value } }))} /></Grid>
              <Grid item xs={12}>
                {dialog.form.contract_id ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button component="label" variant="outlined" size="small">
                      {t('upload_pdf', 'رفع PDF موقّع')}
                      <input type="file" accept="application/pdf" hidden onChange={uploadFile} />
                    </Button>
                    {dialog.form.file_path ? (
                      <Button size="small" onClick={() => viewFile(dialog.form.contract_id)}>{t('view_file', 'عرض الملف')}</Button>
                    ) : <Typography variant="caption" sx={{ color: C.muted }}>{t('no_file', 'لا ملف مرفق')}</Typography>}
                  </Stack>
                ) : (
                  <Typography variant="caption" sx={{ color: C.muted }}>{t('save_then_upload', 'احفظ العقد أولًا لرفع ملف موقّع.')}</Typography>
                )}
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>{t('cancel', 'إلغاء')}</Button>
          <Button variant="contained" onClick={save} disabled={!dialog?.form?.title?.trim()}>{t('save', 'حفظ')}</Button>
        </DialogActions>
      </Dialog>
    </SmartPage>
  );
}
